/** @format */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Page from 'src/components/Page';
import { Link, useHistory, useParams, useLocation } from 'react-router-dom';
import TicketItem from 'src/components/widget/tickets/ticketItem';
import { TablePagination } from '@material-ui/core';
import { ENDPOINTS, TICKET_MODAL, TICKET_STATUS } from 'src/constants';
import axiosInstance from 'src/utils/axios';
import { useDispatch, useSelector } from 'src/store';
import { getCompanyTickets, getSearchTickets, getTickets } from 'src/slices/tickets';
import UpdateTicketModal from 'src/components/widget/tickets/updateTicketModal';
import LoadingScreen from 'src/components/LoadingScreen';
import CloseTicketModal from 'src/components/widget/tickets/closeTicketModal';
import { TicketSearch } from 'src/components/ticket';
import useQuery from 'src/hooks/useQuery';
import Breadcrumb from 'src/components/Breadcrumb';
import { Button } from 'src/components';

// const ticketStatus = ['in_progress', 'closed', 'on_hold', 'canceled', 'submitted'];

export default function Tickets({ isCompany, isSearch }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const query = useQuery().get('query');
  const { t } = useTranslation();
  const { page } = useParams();
  const [filter, setFilter] = useState('all');
  const [pagination, setPagination] = useState({ count: 0, page: 0, rowsPerPage: 5 });
  const { searchTickets, tickets, ticketLoading, error, companyTickets } = useSelector(state => state.tickets);
  const [pageTickets, setPageTickets] = useState((isCompany ? companyTickets : tickets) || null);
  const [currentUpdateTicketId, setCurrentUpdateTicketId] = useState(null);
  const [modalOpened, setModalOpened] = useState(null);

  useEffect(() => {
    if (isSearch) {
      dispatch(getSearchTickets({ query }));
    } else if (isCompany) {
      dispatch(getCompanyTickets());
    } else {
      dispatch(getTickets());
    }
  }, [query, dispatch, isSearch, isCompany]);

  useEffect(() => {
    setPageTickets(isSearch ? searchTickets : isCompany ? companyTickets : tickets || null);
  }, [tickets, companyTickets, searchTickets]);

  useEffect(() => {
    if (pageTickets && pageTickets.length > 0) {
      const count = pageTickets.filter(ticket => {
        if (filter === 'all') return true;
        return ticket.status === filter;
      }).length;
      if (page && page > 0) setPagination(prev => ({ ...prev, count, page: page - 1 }));
      else setPagination(prev => ({ ...prev, count, page: 0 }));
    }
  }, [page, filter, pageTickets]);

  const toggleUpdateTicketModal = incidentid => {
    if (modalOpened === TICKET_MODAL.UPDATE_TICKET) {
      setModalOpened(null);
      setCurrentUpdateTicketId(null);
    } else {
      setModalOpened(TICKET_MODAL.UPDATE_TICKET);
      setCurrentUpdateTicketId(incidentid);
    }
  };
  const toggleCancelTicketModal = incidentid => {
    if (modalOpened === TICKET_MODAL.CLOSE_TICKET) {
      setModalOpened(null);
      setCurrentUpdateTicketId(null);
    } else {
      setModalOpened(TICKET_MODAL.CLOSE_TICKET);
      setCurrentUpdateTicketId(incidentid);
    }
  };
  const handleFilter = _filter => {
    setFilter(_filter);
  };
  const handleBack = e => {
    e.preventDefault();
    if (history.length > 1) {
      history.goBack();
      return;
    }
    history.push(`/dashboard`);
  };

  const start = pagination.page * pagination.rowsPerPage;
  const end = pagination.page * pagination.rowsPerPage + pagination.rowsPerPage;

  const pageTitle = isSearch ? t('Tickets.search') : isCompany ? t('Tickets.myCompanyCases') : t('Tickets.allCases');

  return (
    <Page className={`tickets`} title={pageTitle}>
      <Breadcrumb data={[{ title: t('supportServices'), onClick: handleBack }, { title: pageTitle }]} />
      <div className="container">
        {/* <button onClick={handleBack} className="btn-back">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M5.93534 12.7715C5.45038 12.3715 5.45038 11.6285 5.93534 11.2285L13.8637 4.68933C14.516 4.15137 15.5 4.61532 15.5 5.46078L15.5 18.5392C15.5 19.3847 14.516 19.8486 13.8637 19.3107L5.93534 12.7715Z"
              fill="#234D96"
            />
          </svg>
          <span>{t('Tickets.back')}</span>
        </button> */}
        <div className="tickets-title-box">
          <h1 className="tickets-title">{pageTitle}</h1>
          <TicketSearch />
        </div>
        {ticketLoading && pageTickets === null ? (
          <LoadingScreen />
        ) : (
          pageTickets && (
            <>
              <nav className="ticket-filter">
                <ul>
                  <li className={filter === 'all' ? 'active' : ''}>
                    <button onClick={() => handleFilter('all')}>{t('Tickets.all')}</button>
                  </li>
                  <li className={filter === TICKET_STATUS.new ? 'active' : ''}>
                    <button onClick={() => handleFilter(TICKET_STATUS.new)}>{t('Tickets.new')}</button>
                  </li>
                  <li className={filter === TICKET_STATUS['in progress'] ? 'active' : ''}>
                    <button onClick={() => handleFilter(TICKET_STATUS['in progress'])}>
                      {t('Tickets.inProgress')}
                    </button>
                  </li>
                  <li className={filter === TICKET_STATUS.pending ? 'active' : ''}>
                    <button onClick={() => handleFilter(TICKET_STATUS.pending)}>{t('Tickets.pending')}</button>
                  </li>
                  <li className={filter === TICKET_STATUS.resolved ? 'active' : ''}>
                    <button onClick={() => handleFilter(TICKET_STATUS.resolved)}>{t('Tickets.resolved')}</button>
                  </li>
                  <li className={filter === TICKET_STATUS.cancelled ? 'active' : ''}>
                    <button onClick={() => handleFilter(TICKET_STATUS.cancelled)}>{t('Tickets.cancelled')}</button>
                  </li>
                  <li className={filter === TICKET_STATUS.closed ? 'active' : ''}>
                    <button onClick={() => handleFilter(TICKET_STATUS.closed)}>{t('Tickets.closed')}</button>
                  </li>
                </ul>
              </nav>
              <ol className="tickets-table">
                <li className={`ticket ticket-container in-page ${isCompany ? 'is-company' : ''}`}>
                  <div className="attribute">{t('Tickets.caseID')}</div>
                  <div className="attribute">{t('Tickets.title')}</div>
                  <div className="attribute">{t('Tickets.description')}</div>
                  {isCompany && (
                    <>
                      <div className="attribute">{t('Tickets.customer')}</div>
                      <div className="attribute">{t('Tickets.contact')}</div>
                    </>
                  )}
                  <div className="attribute">{t('Tickets.submitted')}</div>
                  <div className="attribute">{t('Tickets.status')}</div>
                  <div className="attribute"></div>
                </li>
                {pageTickets
                  .filter(ticket => {
                    if (filter === 'all') return true;
                    if (!ticket.status && filter === TICKET_STATUS.new) return true;
                    return ticket.status === filter;
                  })
                  .sort((a, b) => new Date(b.createdon) - new Date(a.createdon))
                  .slice(start, end)
                  .map(ticket => (
                    <TicketItem
                      key={ticket.ticketnumber}
                      inPage
                      ticket={ticket}
                      toggleUpdateTicketModal={toggleUpdateTicketModal}
                      toggleCancelTicketModal={toggleCancelTicketModal}
                      isCompany={isCompany}
                    />
                  ))}
              </ol>
              <div className="table-pagination">
                <TablePagination
                  component="div"
                  count={pagination.count}
                  page={pagination.page}
                  rowsPerPage={pagination.rowsPerPage}
                  onPageChange={(e, page) => {
                    setPagination(prev => ({ ...prev, page }));
                  }}
                  onRowsPerPageChange={e => {
                    setPagination(prev => ({ ...prev, rowsPerPage: e.target.value }));
                  }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                />
              </div>
            </>
          )
        )}
        <Button borderOnly noArrow secondary label={t('backPreviousPage')} onClick={handleBack} />
      </div>
      <UpdateTicketModal
        incidentid={currentUpdateTicketId}
        open={modalOpened === TICKET_MODAL.UPDATE_TICKET}
        onClose={() => {
          setModalOpened(null);
          toggleUpdateTicketModal(null);
        }}
      />
      <CloseTicketModal
        open={modalOpened === TICKET_MODAL.CLOSE_TICKET}
        onClose={() => {
          setModalOpened(null);
          toggleCancelTicketModal(null);
        }}
        incidentid={currentUpdateTicketId}
      />
    </Page>
  );
}
